// Copyright 2018 Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import React from 'react'
import { Link, Redirect } from 'react-router-dom'

// mobx
import { observer } from 'mobx-react'

import { isAuthenticated, downloadFile } from 'services/self'
import { isAIAssistantEnabled } from 'services/config'
import { askDocsAssistant, getStoredSessionId, storeSessionId, getStoredTranscript, storeTranscript, clearStoredSessionId } from 'services/docs-assistant'

// semantic-ui
import { Container, Header, Form, Button, Message, List } from 'semantic-ui-react'

// Backend returns { message } on 400/502/503; anything else (e.g. a network
// failure) has no response body to read a message from.
function errorMessageFrom(error) {
  return (error.response && error.response.data && error.response.data.message) ||
    'Assistant is unavailable, please try again later'
}

// The step 4 handler maps a stale/expired Bedrock session (ValidationException
// on a resumed session) to 409, distinct from the generic 502 for other failures.
function isSessionExpiredError(error) {
  return Boolean(error.response) && error.response.status === 409
}

function speakerLabel(role) {
  if (role === 'user') return 'You'
  if (role === 'assistant') return 'Assistant'
  return 'Note'
}

export default observer(class DocsAssistant extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      question: '',
      sessionId: getStoredSessionId(),
      messages: getStoredTranscript(),
      isAsking: false,
      error: ''
    }
  }

  handleChange = (event, { value }) => this.setState({ question: value })

  handleNewConversation = () => {
    clearStoredSessionId()
    this.setState({ sessionId: undefined, messages: [], error: '' })
  }

  handleSubmit = (event) => {
    event.preventDefault()
    const question = this.state.question.trim()
    if (!question || this.state.isAsking) return

    this.setState({ isAsking: true, error: '' })
    this.submitQuestion(question, this.state.sessionId)
  }

  // isRetry is true only for the one-shot retry after a stale/expired session
  // is cleared, so a second expiry (or any other error) surfaces normally
  // instead of retrying forever.
  submitQuestion = (question, sessionId, isRetry) => {
    askDocsAssistant(question, sessionId)
      .then((result) => {
        const assistantMessage = result.blocked
          ? { role: 'assistant', text: result.message, citations: [] }
          : { role: 'assistant', text: result.answer, citations: result.citations || [] }

        storeSessionId(result.sessionId)

        const newMessages = isRetry
          ? [{ role: 'system', text: 'Your previous conversation had expired, so a new one was started.', citations: [] }]
          : []
        newMessages.push({ role: 'user', text: question, citations: [] }, assistantMessage)
        const messages = (isRetry ? [] : this.state.messages).concat(newMessages)
        storeTranscript(messages)

        this.setState({
          messages,
          question: '',
          sessionId: result.sessionId || this.state.sessionId,
          isAsking: false
        })
      })
      .catch((error) => {
        if (!isRetry && sessionId && isSessionExpiredError(error)) {
          clearStoredSessionId()
          this.setState({ sessionId: undefined, messages: [] })
          this.submitQuestion(question, undefined, true)
          return
        }
        this.setState({ isAsking: false, error: errorMessageFrom(error) })
      })
  }

  render() {
    return (!isAuthenticated() || !isAIAssistantEnabled()) ? <Redirect to='/' /> : (
      <Container className="docs-assistant">
        <div className="docs-assistant__header">
          <Header size="medium">Docs Assistant</Header>
          <Button
            basic
            size="tiny"
            type="button"
            onClick={this.handleNewConversation}
            disabled={this.state.isAsking}
          >
            New conversation
          </Button>
        </div>
        <div className="docs-assistant__transcript" aria-live="polite">
          {this.state.messages.length === 0 && !this.state.isAsking && (
            <p className="docs-assistant__empty">Ask a question about the portal, an API, or a document.</p>
          )}
          {this.state.messages.map((message, index) => (
            <div key={index} className={`docs-assistant__message docs-assistant__message--${message.role}`}>
              <span className="docs-assistant__speaker">{speakerLabel(message.role)}</span>
              <div className="docs-assistant__bubble">
                <p>{message.text}</p>
                {message.citations.length > 0 && (
                  <List className="docs-assistant__citations" bulleted size="small">
                    {message.citations.map((citation, citationIndex) => (
                      <List.Item key={citationIndex}>
                        {citation.key
                          ? <a href="#" onClick={(event) => { event.preventDefault(); downloadFile(citation.key) }}>{citation.title}</a>
                          : citation.apiPath
                            ? <Link to={citation.apiPath}>{citation.title}</Link>
                            : citation.title}
                      </List.Item>
                    ))}
                  </List>
                )}
              </div>
            </div>
          ))}
          {this.state.isAsking && (
            <div className="docs-assistant__message docs-assistant__message--assistant" aria-busy="true">
              <span className="docs-assistant__speaker">Assistant</span>
              <div className="docs-assistant__bubble">
                <p>Looking that up.</p>
              </div>
            </div>
          )}
        </div>
        <Form className="docs-assistant__composer" onSubmit={this.handleSubmit}>
          <Form.TextArea
            placeholder="Ask a question about the developer portal documentation"
            value={this.state.question}
            onChange={this.handleChange}
            disabled={this.state.isAsking}
          />
          <Button
            primary
            type="submit"
            loading={this.state.isAsking}
            disabled={this.state.isAsking || !this.state.question.trim()}
          >
            Ask
          </Button>
        </Form>
        {this.state.error && <Message negative size="small">{this.state.error}</Message>}
      </Container>
    )
  }
})
